import { batch } from 'solid-js';
import { produce, type SetStoreFunction } from 'solid-js/store';
import type { Writable } from 'ts-essentials';
import {
  GeospatialFeature,
  MapWorkspace,
  MapLayer,
  PropertyValue,
  FEATURE_MEMORY_BOUND_METERS,
  findFeatureIdsBeyondMemoryBound,
  type FeatureRetentionRegion,
  type ScenePoint,
} from '@trimble/geospatial';

export type FeatureRetentionRegionReader = () => FeatureRetentionRegion;

export interface FeatureExtractionState {
  initialized: boolean;
  workspaces: MapWorkspace[];
  layers: MapLayer[];
  selectedLayerId: string | undefined;
  selectedFeature: GeospatialFeature | undefined;
  features: Record<string, GeospatialFeature>;
  sceneAnchors: Record<string, ScenePoint>;
  pointCloudCrsById: Record<string, string>;
  projectPointCloudCrs: string | undefined;
  visibleLayerIds: Record<string, true>;
}

export interface FeatureExtractionLoadedLayers {
  workspaces: MapWorkspace[];
  layers: MapLayer[];
  defaultLayerId: string | undefined;
}

export interface FeatureExtractionStateModel {
  upsertFeatures(features: readonly GeospatialFeature[]): void;
  upsertFeature(feature: GeospatialFeature): void;
  mergeFeatureProperties(
    featureId: string,
    values: Readonly<Record<string, PropertyValue>>
  ): void;
  getSceneAnchors(): Readonly<Record<string, ScenePoint>>;
  setSceneAnchors(anchors: Readonly<Record<string, ScenePoint>>): void;
  /** Keeps features near this camera resident; call the result to drop the claim. */
  registerFeatureRetention(read: FeatureRetentionRegionReader): () => void;
  evictDistantFeatures(): void;
  removeFeature(featureId: string): void;
  pointCloudCrs(pointCloudId?: string): string | undefined;
  setPointCloudCrs(pointCloudId: string, crs: string | undefined): void;
  isLayerVisible(layerId: string): boolean;
  toggleLayerVisibility(layerId: string): void;
  applyProjectLayers(data: FeatureExtractionLoadedLayers): void;
}

export interface CreateFeatureExtractionStateModelParams {
  readonly editingState: FeatureExtractionState;
  readonly setEditingState: SetStoreFunction<FeatureExtractionState>;
  bumpTileCacheToken(): void;
}

export function createDefaultFeatureExtractionState(): FeatureExtractionState {
  return {
    initialized: false,
    workspaces: [],
    layers: [],
    selectedLayerId: undefined,
    selectedFeature: undefined,
    features: {},
    sceneAnchors: {},
    pointCloudCrsById: {},
    projectPointCloudCrs: undefined,
    visibleLayerIds: {},
  };
}

export function createFeatureExtractionStateModel(
  params: CreateFeatureExtractionStateModelParams
): FeatureExtractionStateModel {
  const { editingState, setEditingState, bumpTileCacheToken } = params;

  const upsertFeatures = (incoming: readonly GeospatialFeature[]): void => {
    const withIds = incoming.filter(
      (feature): feature is GeospatialFeature & { id: string } =>
        typeof feature.id === 'string' && feature.id.length > 0
    );
    if (withIds.length === 0) {
      return;
    }
    setEditingState(
      'features',
      produce((features) => {
        for (const feature of withIds) {
          const existing = features[feature.id];
          features[feature.id] = existing
            ? {
                ...feature,
                properties: {
                  ...existing.properties,
                  ...feature.properties,
                },
                metadata: {
                  ...existing.metadata,
                  ...feature.metadata,
                },
              }
            : feature;
        }
      })
    );
  };

  const upsertFeature = (feature: GeospatialFeature): void => {
    upsertFeatures([feature]);
  };

  /**
   * Upserting a selection-derived feature would write its scene-space geometry
   * into the WGS-84 map; merging in place also keeps selectedFeature identity
   * so keyed UI stays mounted.
   */
  const mergeFeatureProperties = (
    featureId: string,
    values: Readonly<Record<string, PropertyValue>>
  ): void => {
    const mergeInto = (feature: GeospatialFeature): void => {
      // produce already owns a mutable copy of the readonly feature.
      (feature as Writable<GeospatialFeature>).properties = {
        ...feature.properties,
        ...values,
      };
    };

    setEditingState(
      produce((state) => {
        const feature = state.features[featureId];
        if (feature) {
          mergeInto(feature);
        }
        if (state.selectedFeature?.id === featureId) {
          mergeInto(state.selectedFeature);
        }
      })
    );
  };

  const getSceneAnchors = (): Readonly<Record<string, ScenePoint>> =>
    editingState.sceneAnchors;

  const setSceneAnchors = (
    anchors: Readonly<Record<string, ScenePoint>>
  ): void => {
    if (Object.keys(anchors).length === 0) {
      return;
    }
    setEditingState(
      'sceneAnchors',
      produce((sceneAnchors) => {
        for (const [featureId, anchor] of Object.entries(anchors)) {
          sceneAnchors[featureId] = anchor;
        }
      })
    );
  };

  const retentionRegions = new Set<FeatureRetentionRegionReader>();

  const registerFeatureRetention = (
    read: FeatureRetentionRegionReader
  ): (() => void) => {
    retentionRegions.add(read);
    return () => {
      retentionRegions.delete(read);
    };
  };

  const evictDistantFeatures = (): void => {
    const evictedIds = findFeatureIdsBeyondMemoryBound(
      editingState.sceneAnchors,
      Array.from(retentionRegions, (read) => read()),
      FEATURE_MEMORY_BOUND_METERS
    );
    if (evictedIds.length === 0) {
      return;
    }
    setEditingState(
      produce((state) => {
        for (const featureId of evictedIds) {
          delete state.features[featureId];
          delete state.sceneAnchors[featureId];
          if (state.selectedFeature?.id === featureId) {
            state.selectedFeature = undefined;
          }
        }
      })
    );
  };

  const removeFeature = (featureId: string): void => {
    if (!(featureId in editingState.features)) {
      return;
    }
    batch(() => {
      setEditingState(
        produce((state) => {
          delete state.features[featureId];
          delete state.sceneAnchors[featureId];
        })
      );
      bumpTileCacheToken();
    });
  };

  const pointCloudCrs = (pointCloudId?: string): string | undefined => {
    if (pointCloudId !== undefined) {
      return (
        editingState.pointCloudCrsById[pointCloudId] ??
        editingState.projectPointCloudCrs
      );
    }
    if (editingState.projectPointCloudCrs !== undefined) {
      return editingState.projectPointCloudCrs;
    }
    const perCloudValues = Object.values(editingState.pointCloudCrsById);
    return perCloudValues[0];
  };

  const setPointCloudCrs = (
    pointCloudId: string,
    crs: string | undefined
  ): void => {
    const existing = editingState.pointCloudCrsById[pointCloudId];
    if (crs === undefined) {
      if (existing === undefined) {
        return;
      }
    } else if (existing === crs) {
      return;
    }

    setEditingState(
      'pointCloudCrsById',
      produce((pointCloudCrsById) => {
        if (crs === undefined) {
          delete pointCloudCrsById[pointCloudId];
        } else {
          pointCloudCrsById[pointCloudId] = crs;
        }
      })
    );
  };

  const isLayerVisible = (layerId: string): boolean =>
    editingState.visibleLayerIds[layerId] === true;

  const toggleLayerVisibility = (layerId: string): void => {
    const isVisible = editingState.visibleLayerIds[layerId] === true;
    setEditingState(
      'visibleLayerIds',
      produce((visibleLayerIds) => {
        if (isVisible) {
          delete visibleLayerIds[layerId];
        } else {
          visibleLayerIds[layerId] = true;
        }
      })
    );
  };

  const applyProjectLayers = (data: FeatureExtractionLoadedLayers): void => {
    const previousLayerIds = new Set(
      editingState.layers.map((layer) => layer.id)
    );
    const visibleLayerIds = Object.fromEntries(
      data.layers
        .filter(
          (layer) =>
            !previousLayerIds.has(layer.id) ||
            editingState.visibleLayerIds[layer.id] === true
        )
        .map((layer) => [layer.id, true] as const)
    );

    const currentSelectedId = editingState.selectedLayerId;
    const selectedStillValid =
      currentSelectedId !== undefined &&
      data.layers.some((layer) => layer.id === currentSelectedId);

    setEditingState({
      initialized: true,
      workspaces: data.workspaces,
      layers: data.layers,
      visibleLayerIds,
      selectedLayerId: selectedStillValid
        ? currentSelectedId
        : data.defaultLayerId,
    });
  };

  return {
    upsertFeatures,
    upsertFeature,
    mergeFeatureProperties,
    getSceneAnchors,
    setSceneAnchors,
    registerFeatureRetention,
    evictDistantFeatures,
    removeFeature,
    pointCloudCrs,
    setPointCloudCrs,
    isLayerVisible,
    toggleLayerVisibility,
    applyProjectLayers,
  };
}
